import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { type ClassValue, clsx } from 'clsx';
import { ChevronDown } from './icons';
import {
  getLanguageMeta,
  isAppLanguageCode,
  SUPPORTED_LANGUAGES,
  type AppLanguageCode,
} from './i18n/languages';
import { persistPreferredLocale } from './i18n/persistLocale';
import { resolveAppLanguage } from './useUiLocale';
import { useEnabledLanguages } from './useEnabledLanguages';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type LanguageSelectorProps = {
  className?: string;
  selectClassName?: string;
  /** Compact control for menus; full labelled control for login; header chip for chrome. */
  variant?: 'menu' | 'login' | 'header';
  dark?: boolean;
  /** When true (signed-in), sync selection to Azure SQL preferred_locale. */
  persistToServer?: boolean;
};

function languageOptionLabel(code: AppLanguageCode) {
  const meta = getLanguageMeta(code);
  return `${meta.flag} ${meta.nativeLabel}`;
}

export function LanguageSelector({
  className,
  selectClassName,
  variant = 'menu',
  dark = false,
  persistToServer = false,
}: LanguageSelectorProps) {
  const { t, i18n } = useTranslation();
  const { enabled, selectorsVisible } = useEnabledLanguages();
  const available = SUPPORTED_LANGUAGES.filter((lang) => enabled.includes(lang.code));
  const current = resolveAppLanguage(i18n.language || i18n.resolvedLanguage);
  const currentMeta = getLanguageMeta(current);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const choose = (next: AppLanguageCode) => {
    setOpen(false);
    if (next === current) return;
    if (!enabled.includes(next)) return;
    void (async () => {
      await i18n.changeLanguage(next);
      if (persistToServer) {
        void persistPreferredLocale(next);
      }
    })();
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!selectorsVisible || available.length === 0) {
    return null;
  }

  if (variant === 'login') {
    return (
      <div className={cn('relative w-full', className)} ref={rootRef}>
        <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-white">
          {t('language.label')}
        </label>
        <button
          type="button"
          aria-label={t('language.select')}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((value) => !value)}
          className={cn(
            'flex w-full min-h-11 items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-left text-base text-slate-900 outline-none transition-all focus:ring-2 focus:ring-indigo-500',
            selectClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <span className="text-lg leading-none" aria-hidden="true">
              {currentMeta.flag}
            </span>
            <span className="truncate">{currentMeta.nativeLabel}</span>
          </span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-slate-500 transition-transform', open && 'rotate-180')} />
        </button>
        {open && (
          <ul
            id={listId}
            role="listbox"
            aria-label={t('language.select')}
            className="absolute left-0 right-0 z-30 mt-2 max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl"
          >
            {available.map((lang) => {
              const selected = lang.code === current;
              return (
                <li key={lang.code} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => choose(lang.code)}
                    className={cn(
                      'flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-800 transition-colors',
                      selected ? "bg-indigo-50 text-indigo-900" : "hover:bg-slate-50",
                    )}
                  >
                    <span className="text-lg leading-none" aria-hidden="true">
                      {lang.flag}
                    </span>
                    <span>{lang.nativeLabel}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  if (variant === 'header') {
    return (
      <div className={cn('relative', className)} ref={rootRef}>
        <button
          type="button"
          aria-label={t('language.select')}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((value) => !value)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
            dark
              ? "text-slate-200 hover:bg-slate-800"
              : "text-slate-600 hover:bg-slate-100",
            selectClassName,
          )}
        >
          <span className="text-base leading-none" aria-hidden="true">
            {currentMeta.flag}
          </span>
          <span className="hidden lg:inline max-w-[7rem] truncate">{currentMeta.nativeLabel}</span>
          <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 opacity-70 transition-transform', open && 'rotate-180')} />
        </button>
        {open && (
          <ul
            id={listId}
            role="listbox"
            aria-label={t('language.select')}
            className={cn(
              'absolute right-0 top-full z-50 mt-2 min-w-[12rem] overflow-hidden rounded-xl border py-1 shadow-xl ring-1 ring-slate-900/5',
              dark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white",
            )}
          >
            {available.map((lang) => {
              const selected = lang.code === current;
              return (
                <li key={lang.code} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => choose(lang.code)}
                    className={cn(
                      'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                      dark
                        ? selected
                          ? "bg-slate-800 text-white"
                          : "text-slate-200 hover:bg-slate-800"
                        : selected
                          ? "bg-indigo-50 text-indigo-900"
                          : "text-slate-700 hover:bg-slate-50",
                    )}
                  >
                    <span className="text-base leading-none" aria-hidden="true">
                      {lang.flag}
                    </span>
                    <span>{lang.nativeLabel}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className={cn('relative px-4 py-2', className)} ref={rootRef}>
      <label
        className={cn(
          'mb-1.5 block text-[11px] font-semibold uppercase tracking-wider',
          dark ? "text-slate-400" : "text-slate-500",
        )}
      >
        {t('language.label')}
      </label>
      <button
        type="button"
        aria-label={t('language.select')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm outline-none transition-colors focus:ring-2 focus:ring-indigo-500',
          dark
            ? "border-slate-600 bg-slate-800 text-slate-100"
            : "border-slate-200 bg-white text-slate-800",
          selectClassName,
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-base leading-none" aria-hidden="true">
            {currentMeta.flag}
          </span>
          <span className="truncate">{currentMeta.nativeLabel}</span>
        </span>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 opacity-70 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('language.select')}
          className={cn(
            'absolute left-4 right-4 z-50 mt-1 overflow-hidden rounded-lg border py-1 shadow-lg',
            dark ? "border-slate-600 bg-slate-800" : "border-slate-200 bg-white",
          )}
        >
          {available.map((lang) => {
            const selected = lang.code === current;
            return (
              <li key={lang.code} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => choose(lang.code)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                    dark
                      ? selected
                        ? "bg-slate-700 text-white"
                        : "text-slate-100 hover:bg-slate-700"
                      : selected
                        ? "bg-indigo-50 text-indigo-900"
                        : "text-slate-800 hover:bg-slate-50",
                  )}
                >
                  <span className="text-base leading-none" aria-hidden="true">
                    {lang.flag}
                  </span>
                  <span>{lang.nativeLabel}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <select
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        value={available.some((l) => l.code === current) ? current : available[0]?.code || 'en'}
        onChange={(event) => {
          const next = event.target.value;
          if (isAppLanguageCode(next)) choose(next);
        }}
      >
        {available.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {languageOptionLabel(lang.code)}
          </option>
        ))}
      </select>
    </div>
  );
}
